<script lang="ts">
    import Fa from 'svelte-fa'
    import { onMount } from 'svelte/internal'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import DateSelect from '../_common/date-select.svelte'
    import Popover from '../_common/popover.svelte'
    import type { Charge } from '../../modules/paymentsApi/charges/charge.entity'
    import PaymentsChargeShow from './payments-charge-show.svelte'
    import { ChargeStatus, ChargeStatusFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import { GridBadge, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import { faUser, faEdit } from '@fortawesome/free-solid-svg-icons'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zgrid2 from '../_common/z/zgrid2.svelte'
    // só os acts do Zgrid2 vêm do zgrid2.ts: os types "date"/"select" não existem no GridButton do zgrid.ts
    import { GridButton as Grid2Button, isGrid2Enabled } from '../_common/z/zgrid2'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import Zact from '../_common/z/zact.svelte'
    import { PurchaseType } from '../../modules/paymentsApi/purchases/purchase.enum'
    import PaymentsPurchaseShow from './payments-purchase-show.svelte'
    import dayjs from 'dayjs'

    let show = false
    let entities: Charge[] = []
    let total = 0

    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    let dateSelect

    onMount(async () => {
        await loadEntities()
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    async function dateByOnChange() {
        await loadEntities()
    }

    let dateBy: 'dueDate' | 'paidAt' = 'dueDate'
    async function loadEntities() {
        try {
            show = true
            total = 0
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                purchase: true,
                dateBy,
            }
            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.pay.entries.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (!filter.from || diff > 7) {
                    filter.from = dayjs(new Date()).add(-7, 'day').toDate()
                    if (dateSelect && dates) {
                        dates.from = dates.from
                        dateSelect?.setDate(dates.from, dates.to)
                    }
                }
            }
            entities = await window.api.payments.charges.getAll(filter)
            let personsId = []
            for (let charge of entities) {
                personsId.push(charge.purchase.referenceId)
            }

            let persons: Person[] = []
            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(window.api.events.persons.getAll({ persons: chunk.join(','), removed: true }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            for (let charge of entities) {
                charge.purchase.person = persons.find((el) => el.personId === charge.purchase.referenceId)
            }
            console.log('charges', entities)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let charge = null
    function showChargeDetail(id: string) {
        charge = entities.find((el) => el.chargeId === id)
        console.log('charge', charge)
    }

    function closeChargeId() {
        charge = null
        loadEntities()
    }

    function getFriendlyStatus(charge: Charge) {
        if (charge.status === ChargeStatus.scheduled && charge.tryTimes > 0) {
            return 'atrasado'
        }
        return ChargeStatusFriendly[charge.statusLabel]
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Parcelas'
        grid.subtitle = window.store.token.permissions.pay.entries.navigate ? '' : 'Lançamentos dos últimos 7 dias.'
        // a barra de cima é feita de acts do grid: o período num act type "date" e os dois radios num type "select"
        grid.acts.push(
            new Grid2Button({
                label: 'Período',
                type: 'date',
                dateFrom: dates?.from,
                dateTo: dates?.to,
                callback: (period: { from: Date; to: Date }) => {
                    dates = period
                    loadEntities()
                },
            }),
        )
        // os dois radios viram itens do grupo segmentado (o mesmo do "Por data" no ZanduPay). no celular o grid
        // troca o grupo por um seletor sozinho — isso é por dentro do Zgrid2, não muda nada aqui
        const dateByOptions: ['dueDate' | 'paidAt', string][] = [
            ['dueDate', 'Todas, por data de vencimento'],
            ['paidAt', 'Pagas, por data de pagamento'],
        ]
        for (const [value, label] of dateByOptions) {
            grid.acts.push(
                new Grid2Button({
                    label,
                    active: dateBy === value,
                    callback: () => {
                        dateBy = value
                        loadEntities()
                    },
                }),
            )
        }
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Parcela', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        // grid.cols.push(new GridCol({ content: 'Inclusão', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Retentativa', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Pago em', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Nome', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true, showTotalBox: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.purchase?.referenceId),
                icon: faUser,
                // só o 1º botão da linha fica à mostra; os demais vão pro menu "⋯", onde precisam de texto
                label: 'Pessoa',
            })
            const btnOpenPurchase = new GridButton({
                callback: () => showPurchaseDetail(item.purchaseId),
                icon: faEdit,
                label: 'Cobrança',
            })
            const btnOpenCharge = new GridButton({
                callback: () => showChargeDetail(item.chargeId),
                icon: faEdit,
                label: 'Parcela',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.dueDate })
            line.slots.push({ content: getInstalment(item) })
            line.slots.push({
                content: new GridBadge({ label: ChargeStatusFriendly[item.statusLabel], class: getBadgeColor(item) }),
            })
            // line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.tryNext })
            line.slots.push({ content: item.paidAt })
            line.slots.push({ content: item.purchase.person?.name || '[cliente não informado]' })
            line.slots.push({ content: item.purchase.purchaseDescription })
            line.slots.push({ content: item.value })
            // a "Parcela" (o registro da linha) é a ação principal, por isso vem primeiro
            line.slots.push({ content: [btnOpenCharge, btnOpenPurchase, btnPerson] })
            grid.lines.push(line)
        }
    }

    let purchaseId = null
    function showPurchaseDetail(id: string) {
        purchaseId = id
    }

    function closePurchaseShow() {
        purchaseId = null
        loadEntities()
    }

    function getInstalment(charge: Charge) {
        if (!charge.installmentNumber) return '-'
        if (charge.purchase.purchaseType === PurchaseType.recurring_continuous) {
            return charge.installmentNumber?.toString() + '/∞'
        }
        if (charge.purchase.purchaseType === PurchaseType.recurring_fixed) {
            return charge.installmentNumber?.toString() + '/' + (charge.installmentTotal?.toString() || '-')
        }
        if (charge.purchase.purchaseType === PurchaseType.full_charge) {
            return '-'
        }
    }

    function getBadgeColor(charge: Charge) {
        if (charge.status === ChargeStatus.scheduled && charge.tryTimes > 0) {
            return 'status_overdue'
        }
        if (charge.status === ChargeStatus.paid) return 'status_100'
        if (charge.status === ChargeStatus.paid_manual) return 'status_100'
        if (charge.status === ChargeStatus.pending) return 'status_pending'
        // documento vencido: a dívida continua em aberto, então é aviso, não encerramento
        if (charge.status === ChargeStatus.expired) return 'status_pending'
        return 'status_default'
    }
</script>

{#if purchaseId}
    <Popover title="Cobrança" width="1100px" height="80vh" on:close={closePurchaseShow}>
        <PaymentsPurchaseShow {purchaseId} on:close={closePurchaseShow} />
    </Popover>
{/if}

{#if charge}
    <Popover title="Parcela" width="1100px" height="520px" on:close={closeChargeId}>
        <PaymentsChargeShow personId={charge.purchase?.person?.personId} {charge} on:close={closeChargeId} />
    </Popover>
{/if}
<Content footer={true}>
    <!-- o período e o "Mostrar" são acts do grid (ver loadGrid), por isso não há barra própria aqui -->
    <Zgrid2 class="h-space" {grid} />
    <LoadSpinner {show} />
</Content>

<style>
    .status-10 {
        background-color: #666666;
    }

    .status-20 {
        background-color: #666666;
    }
    .status-80 {
        background-color: #b78505;
    }
    .status-100 {
        background-color: #379e75;
    }
    .status-100 {
        background-color: #5067d2;
    }

    .limit-width-act {
        max-width: 80px;
    }
    .limit-width-status {
        max-width: 162px;
    }
    .limit-width-date {
        max-width: 151px;
    }
    .limit-width-date-min {
        max-width: 111px;
    }
    .form-cities {
        font-family: 'Courier New';
    }

    .go-invoice {
        display: flex;
        align-items: stretch;
        flex-direction: column;
        padding: 16px;
    }

    .go-invoice .btn:first-child {
        margin-bottom: 16px;
    }
</style>
